import { useMemo, useState } from "react";
import { useStateValue } from "../../context/StateProvider";
import {
  ArrowUpRight,
  ArrowDownRight,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Home,
  Tag,
  Trophy,
  Target,
} from "lucide-react";
import * as CC from "../../utils/commandCenter";

const NAVY = "#1E293B";
const THIS_YEAR_COLOR = "#378ADD";
const LAST_YEAR_COLOR = "#94A3B8";

const PERIODS = [
  { id: "ytd", label: "Year to date" },
  { id: "qtd", label: "This quarter" },
  { id: "mtd", label: "This month" },
  { id: "year", label: "Full year" },
  { id: "custom", label: "Custom" },
];

// "YYYY-MM-DD" for <input type="date">, built from local parts (toISOString
// would shift the day backwards in US timezones).
const isoDate = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

// Full figures, never abbreviations: $966,190 rather than $966.19k. Cents are
// dropped because they're noise on aggregate totals.
const money = (v) =>
  Math.round(Number(v) || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

function Delta({ pct }) {
  if (pct == null || !Number.isFinite(pct)) return null;
  const up = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-medium px-2 py-0.5 rounded-full ${
        up ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"
      }`}
    >
      {up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
      {Math.abs(pct).toFixed(0)}%
    </span>
  );
}

function StatTile({ label, value, pct, hint }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="text-2xl font-semibold text-gray-900 mb-2">{value}</p>
      {pct != null ? <Delta pct={pct} /> : hint ? (
        <span className="text-xs text-gray-400">{hint}</span>
      ) : null}
    </div>
  );
}

function GoalBar({ label, actual, goal, format }) {
  const pct = goal > 0 ? Math.min((actual / goal) * 100, 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1.5">
        <span className="text-gray-500">{label}</span>
        <span className="text-gray-900">
          {format(actual)}{" "}
          {goal > 0 && <span className="text-gray-400">/ {format(goal)}</span>}
        </span>
      </div>
      <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, backgroundColor: NAVY }}
        />
      </div>
    </div>
  );
}

function TrendChart({ thisYear, lastYear, year }) {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  // Scale to a clean round ceiling so gridlines land on readable figures.
  const maxVal = CC.niceCeiling(Math.max(...thisYear, ...lastYear, 1)) || 1;
  const W = 620;
  const H = 210;
  const padL = 78;
  const baseY = 170;
  const plotTop = 14;
  const plotH = baseY - plotTop;
  const groupW = (W - padL - 8) / 12;
  const barW = Math.min(15, groupW / 2 - 3);
  const gridlines = [0, 0.5, 1];

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={`Monthly gross commission ${year} versus ${year - 1}`}>
      {gridlines.map((f) => {
        const y = baseY - f * plotH;
        return (
          <g key={f}>
            <line x1={padL} y1={y} x2={W - 4} y2={y} stroke="#E5E7EB" strokeWidth="1" />
            <text x={padL - 8} y={y + 3} fontSize="10" fill="#9CA3AF" textAnchor="end">
              {money(maxVal * f)}
            </text>
          </g>
        );
      })}
      {months.map((m, i) => {
        const gx = padL + i * groupW;
        const lyH = (lastYear[i] / maxVal) * plotH;
        const tyH = (thisYear[i] / maxVal) * plotH;
        const lyX = gx + groupW / 2 - barW - 1;
        const tyX = gx + groupW / 2 + 1;
        return (
          <g key={m}>
            {lastYear[i] > 0 && (
              <rect x={lyX} y={baseY - lyH} width={barW} height={lyH} rx="3" fill={LAST_YEAR_COLOR}>
                <title>{`${m} ${year - 1}: ${money(lastYear[i])}`}</title>
              </rect>
            )}
            {thisYear[i] > 0 && (
              <rect x={tyX} y={baseY - tyH} width={barW} height={tyH} rx="3" fill={THIS_YEAR_COLOR}>
                <title>{`${m} ${year}: ${money(thisYear[i])}`}</title>
              </rect>
            )}
            <text x={gx + groupW / 2} y={baseY + 16} fontSize="10" fill="#6B7280" textAnchor="middle">
              {m}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function CommandCenter() {
  const [{ deals, users }] = useStateValue();
  const now = useMemo(() => new Date(), []);
  const year = now.getFullYear();

  const [period, setPeriod] = useState("ytd");
  const [agentId, setAgentId] = useState("all");
  const [dealType, setDealType] = useState("all");
  const [source, setSource] = useState("all");
  const [customStart, setCustomStart] = useState(() =>
    isoDate(new Date(new Date().getFullYear(), 0, 1)),
  );
  const [customEnd, setCustomEnd] = useState(() => isoDate(new Date()));

  const sourceOptions = useMemo(() => {
    const set = new Set();
    (deals || []).forEach((d) => {
      if (CC.applyDealFilters([d]).length && d.leadsource) set.add(CC.sourceLabel(d.leadsource));
    });
    return Array.from(set).sort();
  }, [deals]);

  const filters = { agentId, dealType, source };
  const range = useMemo(
    () => CC.periodRange(period, now, { start: customStart, end: customEnd }),
    [period, now, customStart, customEnd],
  );

  // Fully-filtered deals drive the headline metrics, trend, funnel and mix.
  const filtered = useMemo(() => CC.applyDealFilters(deals, filters), [deals, agentId, dealType, source]);
  const closed = useMemo(() => CC.closedDealsInRange(filtered, range), [filtered, range]);
  const closedPrior = useMemo(
    () => CC.closedDealsInRange(filtered, CC.priorYearRange(range)),
    [filtered, range],
  );
  const current = useMemo(() => CC.summarize(closed), [closed]);
  const prior = useMemo(() => CC.summarize(closedPrior), [closedPrior]);
  const snap = useMemo(() => CC.pipelineSnapshot(filtered), [filtered]);
  const funnelStages = useMemo(() => CC.funnel(filtered, closed), [filtered, closed]);
  const thisYearMonthly = useMemo(() => CC.monthlyCommission(filtered, year), [filtered, year]);
  const lastYearMonthly = useMemo(() => CC.monthlyCommission(filtered, year - 1), [filtered, year]);
  const mix = useMemo(() => CC.sourceMix(closed), [closed]);

  // The leaderboard always ranks the whole team (type/source/period applied, but
  // NOT the agent filter — otherwise picking an agent would empty the board).
  const boardDeals = useMemo(
    () => CC.applyDealFilters(deals, { dealType, source }),
    [deals, dealType, source],
  );
  const boardClosed = useMemo(() => CC.closedDealsInRange(boardDeals, range), [boardDeals, range]);
  const board = useMemo(() => CC.leaderboard(boardClosed, users, year), [boardClosed, users, year]);

  // Only real, active agents belong in the filter: no deleted, disabled, demo,
  // client or Client Care accounts. Anyone with production is always included so
  // the filter can never hide someone shown on the leaderboard.
  const agentOptions = useMemo(
    () => CC.agentFilterOptions(users, board.map((r) => r.id)),
    [users, board],
  );

  // Goal pace is inherently annual, so it uses current-year YTD and follows the
  // agent scope only (a whole-year goal can't be split by type/source/month).
  const scopeUser = agentId !== "all" ? (users || []).find((u) => u.id === agentId) : null;
  const paceDeals = useMemo(() => CC.applyDealFilters(deals, { agentId }), [deals, agentId]);
  const paceClosed = useMemo(
    () => CC.closedDealsInRange(paceDeals, CC.periodRange("ytd", now)),
    [paceDeals, now],
  );
  const paceSummary = useMemo(() => CC.summarize(paceClosed), [paceClosed]);
  const goalGci = scopeUser ? CC.goalForUser(scopeUser, year) : CC.teamGoal(users, year);
  const goalVol = scopeUser ? CC.goalForUser(scopeUser, year, "volumeGoal") : CC.teamGoal(users, year, "volumeGoal");
  const goalUnits = scopeUser ? CC.goalForUser(scopeUser, year, "unitsGoal") : CC.teamGoal(users, year, "unitsGoal");
  const p = CC.pace(paceSummary.grossCommission, goalGci, now);
  const scopeLabel = scopeUser ? scopeUser.name || CC.agentName(scopeUser) : "Team";

  const maxFunnel = Math.max(...funnelStages.map((s) => s.count), 1);
  // Seven distinct colours, the last reserved for the "Other" roll-up, so no
  // two visible sources ever share a colour.
  const sourceColors = ["#378ADD", "#1D9E75", "#7F77DD", "#D85A30", "#D97706", "#D4537E", "#94A3B8"];
  const mixTop = CC.topSources(mix, 6);

  const selectCls =
    "border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-slate-200";

  return (
    <div className="pb-10">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <div className="flex flex-wrap gap-1.5 bg-white border border-gray-200 rounded-xl p-1 shadow-sm">
          {PERIODS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setPeriod(opt.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                period === opt.id ? "text-white" : "text-gray-500 hover:bg-gray-100"
              }`}
              style={period === opt.id ? { backgroundColor: NAVY } : undefined}
            >
              {opt.id === "ytd" && <Calendar size={14} />}
              {opt.label}
            </button>
          ))}
        </div>
        {period === "custom" && (
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
            <input
              type="date"
              value={customStart}
              max={customEnd}
              onChange={(e) => setCustomStart(e.target.value)}
              aria-label="From date"
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            />
            <span className="text-sm text-gray-400">to</span>
            <input
              type="date"
              value={customEnd}
              min={customStart}
              onChange={(e) => setCustomEnd(e.target.value)}
              aria-label="To date"
              className="text-sm text-gray-700 bg-transparent focus:outline-none"
            />
          </div>
        )}
        <div className="relative flex items-center">
          <Users size={14} className="absolute left-2.5 text-gray-400 pointer-events-none" />
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className={`${selectCls} pl-8`}>
            <option value="all">All agents</option>
            {agentOptions.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>
        <div className="relative flex items-center">
          <Home size={14} className="absolute left-2.5 text-gray-400 pointer-events-none" />
          <select value={dealType} onChange={(e) => setDealType(e.target.value)} className={`${selectCls} pl-8`}>
            <option value="all">All deals</option>
            <option value="Buyer">Buyers</option>
            <option value="Seller">Listings</option>
          </select>
        </div>
        <div className="relative flex items-center">
          <Tag size={14} className="absolute left-2.5 text-gray-400 pointer-events-none" />
          <select value={source} onChange={(e) => setSource(e.target.value)} className={`${selectCls} pl-8`}>
            <option value="all">All sources</option>
            {sourceOptions.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Goal pace hero */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-4">
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <p className="text-sm text-gray-500 mb-1">
              Gross commission pace · {scopeLabel} · {year}
            </p>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold text-gray-900">
                {money(paceSummary.grossCommission)}
              </span>
              {goalGci > 0 && (
                <span className="text-sm text-gray-500">of {money(goalGci)} goal</span>
              )}
            </div>
          </div>
          {p.hasGoal && (
            <span
              className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full ${
                p.ahead ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
              }`}
            >
              {p.ahead ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              {p.ahead ? "Ahead of pace" : "Behind pace"} · projected {money(p.projected)}
            </span>
          )}
        </div>

        {p.hasGoal ? (
          <>
            <div className="relative mt-6 mb-1.5">
              <div
                className="absolute -top-4 text-[11px] text-gray-400 whitespace-nowrap"
                style={{ left: `${Math.min(p.onPacePct, 100)}%`, transform: "translateX(-50%)" }}
              >
                on pace today
              </div>
              <div className="h-3.5 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.min(p.pct, 100)}%`, backgroundColor: p.ahead ? "#059669" : "#D97706" }}
                />
              </div>
              <div
                className="absolute -top-1 w-0.5 h-5"
                style={{ left: `${Math.min(p.onPacePct, 100)}%`, backgroundColor: NAVY }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-400">
              <span>{p.pct.toFixed(0)}% of goal</span>
              <span>{money(goalGci)}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <GoalBar label="Volume" actual={paceSummary.volume} goal={goalVol} format={money} />
              <GoalBar
                label="Closed units"
                actual={paceSummary.units}
                goal={goalUnits}
                format={(v) => Math.round(v)}
              />
            </div>
          </>
        ) : (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
            <Target size={16} />
            Set an annual goal on the Goals page to track pace here.
          </div>
        )}
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <StatTile label="Gross commission" value={money(current.grossCommission)} pct={CC.percentChange(current.grossCommission, prior.grossCommission)} />
        <StatTile label="Sales volume" value={money(current.volume)} pct={CC.percentChange(current.volume, prior.volume)} />
        <StatTile label="Closed units" value={current.units} pct={CC.percentChange(current.units, prior.units)} />
        <StatTile label="Avg sale price" value={money(current.avgSalePrice)} pct={CC.percentChange(current.avgSalePrice, prior.avgSalePrice)} />
        <StatTile label="Pipeline value" value={money(snap.pipelineValue)} hint="under contract + pending" />
      </div>

      {/* Monthly trend */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-4">
        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
          <h3 className="text-base font-semibold text-gray-800">Monthly gross commission</h3>
          <div className="flex gap-4 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: THIS_YEAR_COLOR }} />
              {year}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: LAST_YEAR_COLOR }} />
              {year - 1}
            </span>
          </div>
        </div>
        <TrendChart thisYear={thisYearMonthly} lastYear={lastYearMonthly} year={year} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Pipeline funnel */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Pipeline</h3>
          <div className="flex flex-col gap-2.5">
            {funnelStages.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-500">{s.label}</span>
                  <span className="text-gray-900">{s.count}</span>
                </div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${(s.count / maxFunnel) * 100}%`, backgroundColor: THIS_YEAR_COLOR }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead source mix */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
          <h3 className="text-base font-semibold text-gray-800 mb-4">Lead source mix</h3>
          {mix.length === 0 ? (
            <p className="text-sm text-gray-400">No closed deals in this period.</p>
          ) : (
            <>
              <div className="flex gap-0.5 h-4 mb-4 rounded overflow-hidden">
                {mixTop.map((m, i) => (
                  <div key={m.source} style={{ width: `${m.pct}%`, backgroundColor: sourceColors[i % sourceColors.length] }} />
                ))}
              </div>
              <div className="flex flex-col gap-2 text-sm">
                {mixTop.map((m, i) => (
                  <div key={m.source} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: sourceColors[i % sourceColors.length] }} />
                    <span className="text-gray-500 flex-1 truncate">{m.source}</span>
                    <span className="text-gray-900">{m.pct.toFixed(0)}%</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Agent leaderboard.
          The dollar column here is the WHOLE deal's gross commission on the
          deals this agent is credited with, which is not the same question as
          the Commission Approvals roster, where the dollar column is what each
          person was personally paid. Both figures are correct and both stay as
          they are; the labels below exist so nobody has to guess which is
          which when the two screens rank the same people differently. */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
            <Trophy size={16} className="text-amber-500" />
            Agent leaderboard
          </h3>
          <span className="text-xs text-gray-400 text-right">
            Gross commission (whole deal) · % to goal
          </span>
        </div>
        <p className="text-[11px] text-gray-400 leading-snug mb-3">
          Ranked on the full commission each credited deal earned the team, not
          on what each agent was paid. The Team Roster on Commission Approvals
          ranks on personal earnings, so the same people can appear there in a
          different order.
        </p>
        {board.length === 0 ? (
          <p className="text-sm text-gray-400">No closed deals in this period.</p>
        ) : (
          <div>
            {board.map((row, i) => (
              <div
                key={row.id || i}
                className={`flex items-center gap-3 py-2.5 ${i > 0 ? "border-t border-gray-100" : ""} ${
                  row.id === agentId ? "bg-slate-50 -mx-2 px-2 rounded" : ""
                }`}
              >
                <span className="w-5 text-sm text-gray-400">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-gray-900">{row.name}</span>
                  <div className="h-1.5 rounded-full bg-gray-100 mt-1.5 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${board[0].gci > 0 ? (row.gci / board[0].gci) * 100 : 0}%`, backgroundColor: THIS_YEAR_COLOR }}
                    />
                  </div>
                </div>
                <span className="w-28 text-right text-sm text-gray-900">{money(row.gci)}</span>
                <span className="w-10 text-right text-sm text-gray-400">{row.units}</span>
                <span className="w-14 text-right">
                  {row.pctToGoal != null ? (
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        row.pctToGoal >= 100 ? "bg-emerald-50 text-emerald-600" : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {row.pctToGoal.toFixed(0)}%
                    </span>
                  ) : (
                    <span className="text-xs text-gray-300">—</span>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CommandCenter;
